<script setup lang="ts">
import type { SiteBlock } from '../../types/site'

/**
 * The `<section>` shell around one block — background, text size, container
 * width and vertical spacing from `block.settings`. Split out of
 * BlockRenderer so an editor can wrap each block with its own chrome while
 * rendering exactly the markup the public page uses.
 *
 * `nested` (a block inside a `columns` slot) drops the centered max-width
 * container and horizontal gutter — the column is the container — but keeps
 * spacing, text size and background.
 *
 * `editing` is forwarded to the block component as a plain prop (not an
 * injection — the canvas and the settings panel are siblings, not ancestor
 * and descendant, so there is no shared provide/inject scope between them).
 * A block with no content yet (an empty `cards_grid`, an unset `image` url)
 * uses it to show a "click to configure" placeholder instead of the silent
 * nothing a visitor would see; every block defaults it to false, so public
 * rendering is unaffected whether or not the caller passes it at all.
 *
 * Widths and section spacing resolve to CSS custom properties rather than
 * fixed Tailwind classes, so a consuming app can rescale its whole
 * site-builder surface from its own CSS. Layer defaults for both scales live
 * in assets/css/site-themes.css.
 */
const props = defineProps<{ block: SiteBlock, nested?: boolean, editing?: boolean }>()

const blockRegistry = useSiteBlockRegistry()

const containerVar = (w: string) =>
  `var(--site-container-${['narrow', 'wide', 'full'].includes(w) ? w : 'wide'})`

// `spacing` is optional so blocks stored before this setting existed fall
// back to `m`, which reproduces the original fixed `py-6 sm:py-8` exactly.
const sectionPaddingVar = (s: string | undefined) =>
  `var(--site-section-py-${['none', 's', 'm', 'l'].includes(s ?? '') ? s : 'm'})`

const textSizeClass = (s: string) => ({
  s: 'text-sm',
  m: 'text-base',
  l: 'text-lg'
}[s] ?? 'text-base')

const backgroundClass = (b: string) => ({
  none: '',
  surface: 'bg-[var(--site-surface)] border-y border-[var(--site-border)]',
  accent_soft: 'site-bg-accent-soft'
}[b] ?? '')

// Full-bleed blocks (currently only `announcement`) skip the centered wrapper
// and supply their own padding. They can't be nested, so the flat branch is
// the only one that ever sees them.
const isBleed = computed(() => blockRegistry.descriptor(props.block.type)?.bleed === true)
</script>

<template>
  <section
    :class="[
      backgroundClass(block.settings.background),
      isBleed ? textSizeClass(block.settings.text_size) : ''
    ]"
  >
    <component
      :is="blockRegistry.get(block.type)"
      v-if="isBleed"
      :data="block.data"
      :settings="block.settings"
      :editing="editing"
    />
    <div
      v-else
      :class="[textSizeClass(block.settings.text_size), nested ? '' : 'mx-auto px-6']"
      :style="{
        maxWidth: nested ? undefined : containerVar(block.settings.width),
        paddingBlock: sectionPaddingVar(block.settings.spacing)
      }"
    >
      <component
        :is="blockRegistry.get(block.type)"
        v-if="blockRegistry.has(block.type)"
        :data="block.data"
        :settings="block.settings"
        :editing="editing"
      />
    </div>
  </section>
</template>
